import { useEffect, useState } from 'react';
import { Avatar } from 'antd';
import * as Icons from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useThemeStore } from '../../stores/theme.store';
import { useUIStore } from '../../stores/ui.store';
import { getInitials } from '../../lib/initials';

const BREAKPOINT_MD = 768;

/**
 * Header - Top Navigation Bar
 *
 * Sticky header with dynamic page title and user avatar. The avatar
 * acts as a link to the /profile page.
 */
export function Header() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const pageTitle = usePageTitle();
  const { mode } = useThemeStore();
  const { setMobileMenuOpen } = useUIStore();
  const isDark = mode === 'dark';

  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth < BREAKPOINT_MD
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < BREAKPOINT_MD);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <header
      className={`sticky top-0 z-30 h-14 flex items-center justify-between px-6 ${
        isDark
          ? 'bg-neutral-900 border-b border-neutral-800'
          : 'bg-white'
      }`}
      style={
        !isDark
          ? {
              boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 12px -4px rgba(15, 23, 42, 0.06)',
            }
          : undefined
      }
    >
      {/* Page Title + Mobile Menu Toggle */}
      <div className="flex items-center gap-2">
        {isMobile && (
          <button
            onClick={() => setMobileMenuOpen(true)}
            className={`flex items-center justify-center w-9 h-9 rounded-md transition-colors cursor-pointer ${
              isDark
                ? 'text-neutral-300 hover:text-white hover:bg-white/5'
                : 'text-neutral-700 hover:text-neutral-900 hover:bg-neutral-100'
            }`}
            aria-label="Open menu"
          >
            <Icons.MenuOutlined style={{ fontSize: 16 }} />
          </button>
        )}
        {pageTitle && (
          <h1
            className={`text-base font-semibold ${
              isDark ? 'text-neutral-100' : 'text-neutral-900'
            }`}
          >
            {pageTitle}
          </h1>
        )}
      </div>

      {/* Right Side - Profile Avatar */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/profile')}
          className={`flex items-center gap-2 p-2 rounded-lg transition-colors cursor-pointer ${
            isDark ? 'hover:bg-white/8' : 'hover:bg-neutral-100'
          }`}
        >
          <Avatar size="small" src={user?.avatar} className="bg-indigo-500">
            {getInitials(user?.name, user?.email?.[0]?.toUpperCase() ?? 'U')}
          </Avatar>
          {user?.name && (
            <span
              className={`text-sm font-medium ${
                isDark ? 'text-neutral-300' : 'text-neutral-700'
              }`}
            >
              {user.name}
            </span>
          )}
        </button>
      </div>
    </header>
  );
}
